<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useUserStore } from '@/store/user-store';
import Chart from "primevue/chart";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);const props = defineProps({
  setSection: {
    type: Function,
    required: true,
  },
});

const store = useUserStore();
const attributionData = ref(null);
const isLoading = ref(false);

// Chart data
const stackedBarData = ref({});
const stackedBarOptions = ref({});
const waterfallData = ref({});
const waterfallOptions = ref({});

// Table sorting
const sortColumn = ref('active');
const sortOrder = ref('desc');

// Configuration thresholds
const concentrationThreshold = 30;
const selectionThreshold = -0.05;

// Cache management (same as SectorAttributionOverview)
const CACHE_KEY = 'portfolio_attribution_data';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Register onMounted hook immediately to avoid lifecycle warnings
onMounted(() => {
  // Scroll to top instantly when detail page loads
  window.scrollTo({ top: 0, behavior: 'auto' });

  // Load data immediately on detail page
  nextTick(() => {
    loadAttributionData();
  });
});

// Cache utilities
const getCachedData = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error reading attribution cache:', error);
    return null;
  }
};

const setCachedData = (data) => {
  try {
    const cacheObject = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
  } catch (error) {
    console.error('Error setting attribution cache:', error);
  }
};

const isCacheExpired = (timestamp) => {
  return Date.now() - timestamp > CACHE_DURATION;
};

// Load attribution data with caching
const loadAttributionData = async () => {
  if (isLoading.value) return;

  // Get portfolioId from store's dashboard data
  const portfolioId = store.dashboardData?.portfolioId;
  if (!portfolioId) {
    console.warn('No portfolioId available for attribution analysis');
    return;
  }

  isLoading.value = true;

  try {
    // Create portfolio-specific cache key
    const portfolioCacheKey = `${CACHE_KEY}_${portfolioId}`;

    // Check cache first
    const cached = localStorage.getItem(portfolioCacheKey);
    const cachedData = cached ? JSON.parse(cached) : null;

    if (cachedData && !isCacheExpired(cachedData.timestamp)) {
      console.log('Using cached attribution data (detail page) for portfolio:', portfolioId);
      attributionData.value = cachedData.data;
      setupCharts();
      isLoading.value = false;
      return;
    }

    console.log('Fetching fresh attribution data (detail page) for portfolio:', portfolioId);
    const data = await store.getPortfolioAttributionData(portfolioId);
    console.log('Fetched attribution data:', data);

    // Handle response data structure - attribution data is nested under responseData.attributionData
    if (data && data.responseData && data.responseData.attributionData) {
      attributionData.value = data.responseData.attributionData;
    } else if (data && data.responseData) {
      attributionData.value = data.responseData;
    } else {
      attributionData.value = data;
    }

    // Cache with portfolio-specific key
    const cacheObject = {
      data: attributionData.value,
      timestamp: Date.now()
    };
    localStorage.setItem(portfolioCacheKey, JSON.stringify(cacheObject));

    setupCharts();
  } catch (error) {
    console.error('Error loading attribution data:', error);
    // Show error state if needed
  } finally {
    isLoading.value = false;
  }
};

// Computed: Owned and Unowned sectors
const ownedSectors = computed(() => {
  if (!attributionData.value?.sector_attribution) return [];
  return attributionData.value.sector_attribution.filter(s => s.portfolio_weight > 0);
});
const unownedSectors = computed(() => {
  if (!attributionData.value?.sector_attribution) return [];
  return attributionData.value.sector_attribution.filter(s => s.portfolio_weight === 0);
});

// Filtered: Only sectors with non-zero allocation for graphs
const ownedSectorsForGraph = computed(() => ownedSectors.value.filter(s => s.allocation !== 0 || s.selection !== 0 || s.interaction !== 0));
const unownedSectorsForGraph = computed(() => unownedSectors.value.filter(s => s.allocation !== 0));

// Dynamic width calculation based on data count
const getGridFlexBasis = computed(() => {
  const ownedCount = ownedSectorsForGraph.value.length;
  const unownedCount = unownedSectorsForGraph.value.length;
  const totalCount = ownedCount + unownedCount;

  // If one side has very little data (less than 20% of total), adjust proportions
  if (totalCount === 0) return { owned: '50%', unowned: '50%' };

  const ownedPercent = (ownedCount / totalCount) * 100;
  const unownedPercent = (unownedCount / totalCount) * 100;

  // Minimum 25%, maximum 75% for better balance
  const ownedFlex = Math.max(25, Math.min(75, ownedPercent));
  const unownedFlex = 100 - ownedFlex;

  return { owned: `${ownedFlex}%`, unowned: `${unownedFlex}%` };
});

// Chart data for owned sectors (grouped, not stacked)
const ownedWaterfallData = computed(() => {
  const sectors = ownedSectorsForGraph.value;
  return {
    labels: sectors.map(s => s.sector),
    datasets: [
      {
        label: 'A',
        data: sectors.map(s => s.allocation),
        backgroundColor: sectors.map(s => s.allocation >= 0 ? '#28a745' : '#dc3545'),
        stack: undefined,
      },
      {
        label: 'S',
        data: sectors.map(s => s.selection),
        backgroundColor: sectors.map(s => s.selection >= 0 ? '#28a745' : '#dc3545'),
        stack: undefined,
      },
      {
        label: 'I',
        data: sectors.map(s => s.interaction),
        backgroundColor: sectors.map(s => s.interaction >= 0 ? '#28a745' : '#dc3545'),
        stack: undefined,
      }
    ]
  };
});

// Chart data for unowned sectors (allocation only, only non-zero)
const unownedWaterfallData = computed(() => {
  const sectors = unownedSectorsForGraph.value;
  return {
    labels: sectors.map(s => s.sector),
    datasets: [
      {
        label: 'Allocation',
        data: sectors.map(s => s.allocation),
        backgroundColor: sectors.map(s => s.allocation >= 0 ? '#28a745' : '#dc3545'),
        stack: undefined,
      }
    ]
  };
});

// Chart options for grouped bars with annotation and legend
const ownedWaterfallOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false, // We'll create a custom legend outside the chart
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      borderWidth: 1,
      cornerRadius: 6,
      displayColors: true,
      bodyFont: {
        size: 12,
        weight: 'normal'
      },
      titleFont: {
        size: 13,
        weight: 'bold'
      },
      padding: 12,
      boxWidth: 12,
      boxHeight: 12,
      usePointStyle: true,
      multiKeyBackground: 'transparent',
      callbacks: {
        label: function(context) {
          const label = context.dataset.label;
          const value = context.parsed.y;
          let description = '';

          switch(label) {
            case 'A':
              description = 'Allocation: Weight vs benchmark impact';
              break;
            case 'S':
              description = 'Selection: Stock picking performance';
              break;
            case 'I':
              description = 'Interaction: Combined allocation-selection effect';
              break;
          }

          return `${description}: ${value.toFixed(2)}%`;
        },
        afterBody: function(tooltipItems) {
          const index = tooltipItems[0].dataIndex;
          const sectorData = ownedSectorsForGraph.value[index];
          if (!sectorData) return [];

          return [
            '',
            `Portfolio Weight: ${sectorData.portfolio_weight.toFixed(1)}%`,
            `Benchmark Weight: ${sectorData.benchmark_weight.toFixed(1)}%`,
            `Weight Difference: ${(sectorData.portfolio_weight - sectorData.benchmark_weight).toFixed(1)}%`,
            '',
            `Portfolio Return: ${sectorData.portfolio_return.toFixed(2)}%`,
            `Benchmark Return: ${sectorData.benchmark_return.toFixed(2)}%`,
          ];
        }
      }
    }
  },
  scales: {
    x: {
      stacked: false,
      grid: { display: true, color: '#f0f0f0' },
      ticks: {
        font: { size: 10 },
        maxRotation: 45,
        minRotation: 0
      },
      barPercentage: 0.8,
      categoryPercentage: 0.9
    },
    y: {
      stacked: false,
      beginAtZero: true,
      grid: { display: true, color: '#f0f0f0' },
      ticks: {
        callback: function(value) { return value.toFixed(1) + '%'; }
      }
    }
  }
}));

// Setup charts
const setupCharts = () => {
  setupStackedBar();
  setupWaterfallChart();
};

// Setup stacked attribution bar (same as overview)
const setupStackedBar = () => {
  if (!attributionData.value?.summary) return;

  const summary = attributionData.value.summary;

  stackedBarData.value = {
    labels: ['Attribution Breakdown'],
    datasets: [
      {
        label: 'Allocation',
        data: [summary.allocation_total],
        backgroundColor: '#28a745',
        borderWidth: 0,
      },
      {
        label: 'Selection',
        data: [summary.selection_total],
        backgroundColor: summary.selection_total >= 0 ? '#ffc107' : '#fd7e14',
        borderWidth: 0,
      },
      {
        label: 'Interaction',
        data: [summary.interaction_total],
        backgroundColor: summary.interaction_total >= 0 ? '#17a2b8' : '#6f42c1',
        borderWidth: 0,
      }
    ]
  };

  stackedBarOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        grid: {
          display: true,
          color: '#e9ecef',
        },
        ticks: {
          callback: function(value) {
            return value.toFixed(2) + '%';
          }
        }
      },
      y: {
        stacked: true,
        display: false,
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: 600
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label;
            const value = context.parsed.x;
            let definition = '';

            switch(label) {
              case 'Allocation':
                definition = 'Asset allocation decisions vs benchmark';
                break;
              case 'Selection':
                definition = 'Security selection within sectors';
                break;
              case 'Interaction':
                definition = 'Combined effect of allocation and selection';
                break;
            }

            return `${label}: ${value.toFixed(2)}% - ${definition}`;
          }
        }
      }
    }
  };
};

// Setup waterfall/tornado chart
const setupWaterfallChart = () => {
  if (!attributionData.value?.sector_attribution) return;

  const sectors = attributionData.value.sector_attribution
    .filter(sector => sector.portfolio_weight > 0 || Math.abs(sector.allocation + sector.selection + sector.interaction) > 0.01)
    .map(sector => ({
      ...sector,
      total: sector.allocation + sector.selection + sector.interaction
    }))
    .sort((a, b) => b.total - a.total);

  const labels = sectors.map(s => s.sector);

  waterfallData.value = {
    labels: labels,
    datasets: [
      {
        label: 'Allocation',
        data: sectors.map(s => s.allocation),
        backgroundColor: '#28a745',
        stack: 'sector',
      },
      {
        label: 'Selection',
        data: sectors.map(s => s.selection),
        backgroundColor: sectors.map(s => s.selection >= 0 ? '#ffc107' : '#fd7e14'),
        stack: 'sector',
      },
      {
        label: 'Interaction',
        data: sectors.map(s => s.interaction),
        backgroundColor: sectors.map(s => s.interaction >= 0 ? '#17a2b8' : '#6f42c1'),
        stack: 'sector',
      }
    ]
  };

  waterfallOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: false,
        grid: {
          display: true,
          color: '#f0f0f0',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 10
          }
        },
        barPercentage: 0.8,
        categoryPercentage: 0.8
      },
      y: {
        stacked: false,
        beginAtZero: true,
        grid: {
          display: true,
          color: '#f0f0f0',
        },
        ticks: {
          callback: function(value) {
            return value.toFixed(2) + '%';
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false, // Hide legend for unowned sectors chart
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: true,
        bodyFont: {
          size: 12,
          weight: 'normal'
        },
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        padding: 12,
        boxWidth: 12,
        boxHeight: 12,
        usePointStyle: true,
        multiKeyBackground: 'transparent',
        mode: 'index',
        intersect: false,
        callbacks: {
          afterBody: function(tooltipItems) {
            const index = tooltipItems[0].dataIndex;
            // Get the correct sector data from the filtered unowned sectors
            const sectorData = unownedSectorsForGraph.value[index];
            if (!sectorData) return [];

            // Split long advisor cue text into multiple lines
            const advisorCue = getAdvisorCue(sectorData);
            const wrappedCue = advisorCue ? advisorCue.match(/.{1,50}(\s|$)/g) || [advisorCue] : ['No significant impact'];

            return [
              '',
              `Portfolio Weight: ${(sectorData.portfolio_weight || 0).toFixed(1)}%`,
              `Benchmark Weight: ${(sectorData.benchmark_weight || 0).toFixed(1)}%`,
              `Portfolio Return: ${(sectorData.portfolio_return || 0).toFixed(2)}%`,
              `Benchmark Return: ${(sectorData.benchmark_return || 0).toFixed(2)}%`,
              '',
              ...wrappedCue
            ];
          }
        }
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    }
  };
};

// Get advisor cue for sector
const getAdvisorCue = (sector) => {
  const isOverweight = sector.portfolio_weight > sector.benchmark_weight;
  const sectorOutperformed = sector.benchmark_return > attributionData.value.summary.benchmark_total_return;
  const portfolioOutperformed = sector.portfolio_return > sector.benchmark_return;

  let allocationCue = '';
  let selectionCue = '';

  // Allocation cue
  if (sector.allocation > 0.05) {
    allocationCue = `Allocation ↑ You are ${isOverweight ? 'overweight' : 'underweight'} ${sector.sector}. ${sectorOutperformed ? 'Sector beat' : 'Sector lagged'} the index, so allocation ${sector.allocation > 0 ? 'helped' : 'hurt'}.`;
  } else if (sector.allocation < -0.05) {
    allocationCue = `Allocation ↓ ${isOverweight ? 'Overweight position' : 'Underweight position'} in ${sector.sector} hurt performance.`;
  }

  // Selection cue
  if (sector.selection > 0.05) {
    selectionCue = `Selection ↑ Your ${sector.sector} stocks outperformed the sector benchmark by ${(sector.portfolio_return - sector.benchmark_return).toFixed(2)}%.`;
  } else if (sector.selection < -0.05) {
    selectionCue = `Selection ↓ Your ${sector.sector} stocks underperformed; consider stock review.`;
  }

  return [allocationCue, selectionCue].filter(cue => cue).join(' ');
};

// Table data with sorting
const tableData = computed(() => {
  if (!attributionData.value?.sector_attribution) return [];

  let data = [...attributionData.value.sector_attribution].map(sector => ({
    ...sector,
    active: sector.allocation + sector.selection + sector.interaction,
    weightDiff: sector.portfolio_weight - sector.benchmark_weight,
    returnDiff: sector.portfolio_return - sector.benchmark_return
  }));

  // Sort data
  data.sort((a, b) => {
    const aVal = a[sortColumn.value];
    const bVal = b[sortColumn.value];

    if (sortOrder.value === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  });

  return data;
});

// Sort table
const sortTable = (column) => {
  if (sortColumn.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortOrder.value = 'desc';
  }
};

// Get sort icon
const getSortIcon = (column) => {
  if (sortColumn.value !== column) return '↕';
  return sortOrder.value === 'asc' ? '↑' : '↓';
};

// Badge helpers
const hasConcentrationRisk = (weight) => weight > concentrationThreshold;
const hasSelectionDrag = (selection) => selection < selectionThreshold;
const getWeightBadge = (diff) => {
  if (Math.abs(diff) < 1) return null;
  return diff > 0 ? '↗' : '↘';
};

// Format percentage
const formatPercentage = (value, decimals = 2, showSign = false) => {
  if (value === null || value === undefined) return '0.00%';
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
};

// Get current month-year
const getCurrentMonthYear = () => {
  const date = new Date();
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Navigate back to Portfolio Analytics Overview
const goBack = () => {
  props.setSection(1);
};
</script>

<template>
  <div class="sector-analytics-detail">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <button class="back-button" @click="goBack">
          ← Back to Overview
        </button>
        <h1 class="page-title">Sector Attribution Analysis</h1>
        <span class="analysis-date">{{ getCurrentMonthYear() }}</span>
      </div>
    </div>

    <!-- Stacked Attribution Bar (Same as Overview) -->
    <div class="attribution-bar-section mb-30" v-if="attributionData">
      <div class="section-card">
        <div class="attribution-microcopy emphasized-microcopy">
          <p class="microcopy-text">
            Your active return of {{ formatPercentage(attributionData.summary.excess_return, 2, true) }}
            comes from {{ formatPercentage(attributionData.summary.allocation_total, 2, true) }} Allocation,
            {{ formatPercentage(attributionData.summary.selection_total, 2, true) }} Selection,
            {{ formatPercentage(attributionData.summary.interaction_total, 2, true) }} Interaction.
          </p>
        </div>
      </div>
    </div>

    <!-- Waterfall Chart Section (Split View, Improved Layout) -->
    <div class="waterfall-section mb-30" v-if="attributionData">
      <div class="section-card waterfall-split-better">
        <div class="sector-legend-inline-restyled">
          <span class="dot concentration-dot"></span>
          <span class="legend-label-restyled">Concentration Risk (&gt;{{ concentrationThreshold }}%)</span>
        </div>

        <!-- Custom Legend and Guide - Now positioned before the sector grids -->
        <div class="chart-legend-guide">
          <div class="legend-title">Attribution Components (A + S + I = Total Impact)</div>
          <div class="legend-items">
            <div class="legend-item-modern">
              <div class="legend-bars">
                <div class="mini-bar positive" title="Positive Impact"></div>
                <div class="mini-bar negative" title="Negative Impact"></div>
              </div>
              <div class="legend-content">
                <span class="legend-label-bold">A = Allocation</span>
                <span class="legend-description">Weight vs benchmark impact</span>
              </div>
            </div>
            <div class="legend-item-modern">
              <div class="legend-bars">
                <div class="mini-bar positive"></div>
                <div class="mini-bar negative"></div>
              </div>
              <div class="legend-content">
                <span class="legend-label-bold">S = Selection</span>
                <span class="legend-description">Stock picking performance</span>
              </div>
            </div>
            <div class="legend-item-modern">
              <div class="legend-bars">
                <div class="mini-bar positive"></div>
                <div class="mini-bar negative"></div>
              </div>
              <div class="legend-content">
                <span class="legend-label-bold">I = Interaction</span>
                <span class="legend-description">Combined A×S effect</span>
              </div>
            </div>
          </div>
          <div class="legend-footer">
            <div class="color-guide">
              <span class="color-indicator positive-color"></span>
              <span class="color-text">Positive Impact</span>
              <span class="color-indicator negative-color"></span>
              <span class="color-text">Negative Impact</span>
            </div>
          </div>
        </div>

        <div class="split-titles">
          <div class="split-title-left" :style="{ width: getGridFlexBasis.owned }">
            Owned Sectors — Allocation + Selection + Interaction
          </div>
          <div class="split-title-right" :style="{ width: getGridFlexBasis.unowned }">Unowned Sectors — Allocation Only</div>
        </div>

        <div class="split-grids">
          <!-- Owned Sectors Grid -->
          <div class="sector-grid left-grid owned-sectors-bg" :style="{ flex: `0 0 ${getGridFlexBasis.owned}` }" :class="{ 'sparse-grid': ownedSectorsForGraph.length <= 3 }">
            <div v-for="sector in ownedSectorsForGraph" :key="sector.sector" class="sector-grid-item" style="flex-direction:column;align-items:flex-start;gap:0;">
              <div style="display:flex;align-items:center;gap:8px;width:100%;">
                <span class="sector-name" :title="sector.sector" style="font-size:0.75rem;">
                  {{ sector.sector.length > 11 ? sector.sector.slice(0, 11) + '...' : sector.sector }}
                </span>
                <span v-if="hasConcentrationRisk(sector.portfolio_weight)" class="dot concentration-dot" title="Concentration Risk"></span>
                <span class="sector-value" style="font-size:0.75rem;">{{ formatPercentage(sector.allocation + sector.selection + sector.interaction, 2, true) }}</span>
              </div>
              <div v-if="sector.stocks && sector.stocks.length" class="sector-stocks-row" style="font-size:0.72rem;color:#888;margin-top:2px;white-space:normal;">
                <span>Stocks: </span>
                <span>{{ sector.stocks.map(s => s.name).join(', ') }}</span>
              </div>
            </div>
          </div>
          <!-- Unowned Sectors Grid -->
          <div class="sector-grid right-grid right-align-grid unowned-sectors-bg" :style="{ flex: `0 0 ${getGridFlexBasis.unowned}` }" :class="{ 'sparse-grid': unownedSectorsForGraph.length <= 3 }">
            <div v-for="sector in unownedSectorsForGraph" :key="sector.sector" class="sector-grid-item">
              <div style="display:flex;align-items:center;gap:8px;width:100%;">
                <span class="sector-name" :title="sector.sector" style="font-size:0.75rem;">
                  {{ sector.sector.length > 11 ? sector.sector.slice(0, 11) + '...' : sector.sector }}
                </span>
                <span class="sector-value" style="font-size:0.75rem;">{{ formatPercentage(sector.allocation, 2, true) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="split-charts-better">
          <!-- Left: Owned Sectors Chart -->
          <div class="split-panel-better left-panel-better owned-chart-bg" :style="{ flex: `0 0 ${getGridFlexBasis.owned}` }">
            <Chart
              type="bar"
              :data="ownedWaterfallData"
              :options="ownedWaterfallOptions"
              class="waterfall-chart"
            />
          </div>
          <!-- Right: Unowned Sectors Chart -->
          <div class="split-panel-better right-panel-better unowned-chart-bg" :style="{ flex: `0 0 ${getGridFlexBasis.unowned}` }">
            <Chart
              type="bar"
              :data="unownedWaterfallData"
              :options="waterfallOptions"
              class="waterfall-chart"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Sector Table Section -->
    <div class="sector-table-section" v-if="attributionData">
      <div class="section-card">
        <div class="table-header">
          <div class="table-header-content">
            <h2 class="section-title">Sector Analysis Table</h2>
            <p class="section-subtitle">Detailed breakdown with micro-badges and sorting</p>
          </div>
          <div class="legend">
            <div class="legend-item">
              <span class="concentration-badge">C</span>
              <span class="legend-text">Concentration Risk (>{{ concentrationThreshold }}%)</span>
            </div>
            <div class="legend-item">
              <span class="selection-badge">S</span>
              <span class="legend-text">Selection Drag (&lt;{{ (selectionThreshold * 100).toFixed(1) }}%)</span>
            </div>
            <div class="legend-item">
              <span class="weight-badge">↗↘</span>
              <span class="legend-text">Over/Underweight vs Benchmark</span>
            </div>
          </div>
        </div>

        <div class="table-container">
          <table class="sector-table">
            <thead>
              <tr>
                <th class="sector-col">
                  <button @click="sortTable('sector')" class="sort-button">
                    Sector {{ getSortIcon('sector') }}
                  </button>
                </th>
                <th class="weight-col">
                  <button @click="sortTable('portfolio_weight')" class="sort-button">
                    P Wt. {{ getSortIcon('portfolio_weight') }}
                  </button>
                </th>
                <th class="weight-col">
                  <button @click="sortTable('benchmark_weight')" class="sort-button">
                    B Wt. {{ getSortIcon('benchmark_weight') }}
                  </button>
                </th>
                <th class="return-col">
                  <button @click="sortTable('portfolio_return')" class="sort-button">
                    P Ret. {{ getSortIcon('portfolio_return') }}
                  </button>
                </th>
                <th class="return-col">
                  <button @click="sortTable('benchmark_return')" class="sort-button">
                    B Ret. {{ getSortIcon('benchmark_return') }}
                  </button>
                </th>
                <th class="attribution-col">
                  <button @click="sortTable('allocation')" class="sort-button">
                    Allocation {{ getSortIcon('allocation') }}
                  </button>
                </th>
                <th class="attribution-col">
                  <button @click="sortTable('selection')" class="sort-button">
                    Selection {{ getSortIcon('selection') }}
                  </button>
                </th>
                <th class="attribution-col">
                  <button @click="sortTable('interaction')" class="sort-button">
                    Interaction {{ getSortIcon('interaction') }}
                  </button>
                </th>
                <th class="active-col">
                  <button @click="sortTable('active')" class="sort-button">
                    Active (Sum) {{ getSortIcon('active') }}
                  </button>
                </th>
                <th class="advisor-col">Advisor Cue</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sector in tableData" :key="sector.sector" class="table-row">
                <td class="sector-cell">
                  <div class="sector-name-container">
                    <span class="sector-name">{{ sector.sector }}</span>
                    <div class="micro-badges">
                      <span v-if="hasConcentrationRisk(sector.portfolio_weight)"
                            class="concentration-badge"
                            title="Concentration Risk">C</span>
                      <span v-if="hasSelectionDrag(sector.selection)"
                            class="selection-badge"
                            title="Selection Drag">S</span>
                      <span v-if="getWeightBadge(sector.weightDiff)"
                            class="weight-badge"
                            :title="`${sector.weightDiff > 0 ? 'Overweight' : 'Underweight'} by ${Math.abs(sector.weightDiff).toFixed(1)}%`">
                        {{ getWeightBadge(sector.weightDiff) }}
                      </span>
                    </div>
                  </div>
                </td>
                <td class="weight-cell" :class="{ 'has-position': sector.portfolio_weight > 0 }">
                  {{ formatPercentage(sector.portfolio_weight, 1) }}
                </td>
                <td class="weight-cell">
                  {{ formatPercentage(sector.benchmark_weight, 1) }}
                </td>
                <td class="return-cell" :class="{ 'positive': sector.portfolio_return > 0, 'negative': sector.portfolio_return < 0 }">
                  {{ formatPercentage(sector.portfolio_return, 2, true) }}
                </td>
                <td class="return-cell" :class="{ 'positive': sector.benchmark_return > 0, 'negative': sector.benchmark_return < 0 }">
                  {{ formatPercentage(sector.benchmark_return, 2, true) }}
                </td>
                <td class="attribution-cell" :class="{ 'positive': sector.allocation > 0, 'negative': sector.allocation < 0 }">
                  {{ formatPercentage(sector.allocation, 2, true) }}
                </td>
                <td class="attribution-cell" :class="{ 'positive': sector.selection > 0, 'negative': sector.selection < 0 }">
                  {{ formatPercentage(sector.selection, 2, true) }}
                </td>
                <td class="attribution-cell" :class="{ 'positive': sector.interaction > 0, 'negative': sector.interaction < 0 }">
                  {{ formatPercentage(sector.interaction, 2, true) }}
                </td>
                <td class="active-cell" :class="{ 'positive': sector.active > 0, 'negative': sector.active < 0 }">
                  <strong>{{ formatPercentage(sector.active, 2, true) }}</strong>
                </td>
                <td class="advisor-cell">
                  <div class="advisor-cue">{{ getAdvisorCue(sector) || 'No significant impact' }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading detailed sector attribution data...</p>
      </div>
    </div>

    <!-- Placeholder State (only show when not loading and no data) -->
    <div v-else-if="!attributionData" class="placeholder-state">
      <div class="placeholder-content">
        <div class="placeholder-icon">📊</div>
        <h4 class="placeholder-title">Loading Sector Attribution Data</h4>
        <p class="placeholder-message">
          Detailed attribution analysis will appear here
        </p>
      </div>
    </div>

    <!-- Attribution Explainer Accordion -->
    <div class="attribution-explainer" v-if="attributionData">
      <details class="explainer-accordion">
        <summary class="explainer-summary">
          <span class="explainer-heading">How to Read Attribution (Allocation • Selection • Interaction)</span>
          <span class="explainer-icon">▼</span>
        </summary>
        <div class="explainer-content">
          <div class="explainer-block">
            <h4 class="explainer-block-title">Allocation</h4>
            <p class="explainer-block-text">
              Where you invested more/less. Adds value when you overweight sectors that outperform and underweight sectors that lag.
            </p>
          </div>
          <div class="explainer-block">
            <h4 class="explainer-block-title">Selection</h4>
            <p class="explainer-block-text">
              What you picked within the sector. Measures whether your stocks in a sector beat the sector benchmark (independent of sector weight).
            </p>
          </div>
          <div class="explainer-block">
            <h4 class="explainer-block-title">Interaction</h4>
            <p class="explainer-block-text">
              Conviction × selection. Captures whether your overweight/underweight amplified good or bad stock selection.
            </p>
            <p class="explainer-block-italic">Think: Being right — and backing it.</p>
          </div>
          <div class="explainer-footer">
            Attribution compares your portfolio vs the selected benchmark for this period; it explains drivers, not guarantees.
          </div>
        </div>
      </details>
    </div>
  </div>
</template>

<style scoped>
.sector-analytics-detail {
  width: 100%;
  padding: 20px 20px 40px 20px;
}

/* Emphasized microcopy style */
.emphasized-microcopy .microcopy-text {
  font-size: 0.98rem;
  font-weight: bold;
  color: var(--an-text-primary);
  margin: 0;
  line-height: 1.5;
}

/* Page Header */
.page-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--an-primary);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.back-button {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.77rem;
  font-weight: 600;
  color: var(--an-text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-button:hover {
  background: #e9ecef;
  transform: translateX(-2px);
}

.page-title {
  font-size: 1.53rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
}

.analysis-date {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--an-text-muted);
  background: #f8f9fa;
  padding: 6px 12px;
  border-radius: 15px;
  border: 1px solid #e9ecef;
}

/* Section Cards */
.section-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 25px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 8px;
}

.section-subtitle {
  font-size: 0.9rem;
  color: var(--an-text-muted);
  margin-bottom: 20px;
}

/* Attribution Bar Section */
.stacked-bar-container {
  height: 120px;
  margin-bottom: 20px;
}

.attribution-chart {
  height: 100%;
  width: 100%;
}

.attribution-microcopy {
  text-align: center;
  padding: 15px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.microcopy-text {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0;
  line-height: 1.4;
}

/* Waterfall Chart */
.waterfall-container {
  height: 350px;
}

.waterfall-chart {
  height: 322.58px;
  width: 100%;
}

/* Chart.js tooltip text wrapping styles */
:global(.chartjs-tooltip) {
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
  max-width: 300px !important;
  text-align: left !important;
}

:global(.chartjs-tooltip-body) {
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
  line-height: 1.4 !important;
}

:global(.chartjs-tooltip .chartjs-tooltip-body div) {
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
  max-width: 280px !important;
}

/* Table Section */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.table-header-content h2 {
  margin-bottom: 4px;
}

.legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-text {
  font-size: 0.85rem;
  color: var(--an-text-muted);
}

/* Table Styling */
.table-container {
  overflow-x: auto;
}

.sector-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.sector-table th {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  padding: 12px 8px;
  text-align: center;
  font-weight: 600;
}

.sector-table td {
  border: 1px solid #f0f0f0;
  padding: 8px;
  text-align: center;
  vertical-align: middle;
}

.sort-button {
  background: none;
  border: none;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-primary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.sort-button:hover {
  background: #e9ecef;
}

/* Column Specific Styling */
.sector-col { width: 15%; }
.weight-col { width: 8%; }
.return-col { width: 8%; }
.attribution-col { width: 10%; }
.active-col { width: 10%; }
.advisor-col { width: 25%; }

.sector-cell {
  text-align: left !important;
  padding: 12px 8px;
}

.sector-name-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.sector-name {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 0.85rem;
  min-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  white-space: normal;
  max-width: 220px;
}

.micro-badges {
  display: flex;
  gap: 4px;
}

/* Micro Badges */
.concentration-badge,
.selection-badge,
.weight-badge {
  font-size: 0.85rem;
  font-weight: 700;
  padding: 2px 4px;
  border-radius: 3px;
  line-height: 1;
}

.concentration-badge {
  background: #ff6b6b;
  color: white;
}

.selection-badge {
  background: #ffa726;
  color: white;
}

.weight-badge {
  background: #42a5f5;
  color: white;
}

.legend .concentration-badge,
.legend .selection-badge,
.legend .weight-badge {
  min-width: 16px;
  text-align: center;
}

/* Cell Value Styling */
.has-position {
  background-color: #e8f5e8;
  font-weight: 700;
}

.positive {
  color: #28a745;
  font-weight: 600;
}

.negative {
  color: #dc3545;
  font-weight: 600;
}

.active-cell {
  background: #f8f9fa;
  font-size: 0.9rem;
}

.advisor-cell {
  text-align: left !important;
  padding: 12px 8px;
}

.advisor-cue {
  font-size: 0.8rem;
  line-height: 1.3;
  color: var(--an-text-primary);
}

.table-row:hover {
  background-color: #f8f9fa;
}

/* Loading State */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid var(--an-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  font-size: 16px;
  color: var(--an-text-muted);
  margin: 0;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Placeholder State */
.placeholder-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.placeholder-content {
  text-align: center;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 16px;
}

.placeholder-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.placeholder-message {
  font-size: 1rem;
  color: var(--an-text-muted);
  margin: 0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .table-header {
    flex-direction: column;
    gap: 15px;
  }

  .sector-table {
    font-size: 0.8rem;
  }

  .advisor-col {
    width: 30%;
  }
}

@media (max-width: 768px) {
  .sector-analytics-detail {
    padding: 15px;
  }

  .section-card {
    padding: 20px 15px;
  }

  .page-title {
    font-size: 1.4rem;
  }

  .table-container {
    max-height: 400px;
    overflow-y: auto;
  }

  .sector-table {
    font-size: 0.75rem;
  }

  .sector-table th,
  .sector-table td {
    padding: 6px 4px;
  }

  .advisor-cue {
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .waterfall-container {
    height: 300px;
  }

  .stacked-bar-container {
    height: 100px;
  }

  .legend {
    flex-direction: column;
  }

  .legend-item {
    justify-content: flex-start;
  }
}

.waterfall-split {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.split-titles {
  display: flex;
  justify-content: space-between;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 10px;
}
.split-title-left, .split-title-right {
  width: 50%;
  min-width: 180px;
  text-align: center;
  font-size: 0.935rem;
}
.split-charts {
  display: flex;
  gap: 20px;
}
.split-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.bar-labels {
  width: 100%;
  margin-bottom: 8px;
}
.bar-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 2px;
}
.bar-label-text {
  color: var(--an-text-primary);
}
.badge.concentration-badge {
  background: #ff6b6b;
  color: #fff;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.68rem;
  font-weight: 700;
}
.split-caption {
  text-align: center;
  font-size: 0.95rem;
  color: var(--an-text-muted);
  margin-top: 10px;
}
.left-panel .waterfall-chart, .right-panel .waterfall-chart,
.left-panel-better .waterfall-chart, .right-panel-better .waterfall-chart {
  width: 100%;
  height: 322.58px;
}
@media (max-width: 900px) {
  .split-charts {
    flex-direction: column;
  }
  .split-title-left, .split-title-right {
    width: 100%;
  }
}

/* Custom Chart Legend and Guide */
.chart-legend-guide {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.legend-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--an-text-primary);
  text-align: center;
  margin-bottom: 12px;
}

.legend-items {
  display: flex;
  justify-content: space-around;
  gap: 16px;
  margin-bottom: 10px;
}

.legend-item-modern {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.legend-bars {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 16px;
}

.mini-bar {
  width: 16px;
  height: 6px;
  border-radius: 2px;
}

.mini-bar.positive {
  background: #28a745;
}

.mini-bar.negative {
  background: #dc3545;
}

.legend-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.legend-label-bold {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--an-text-primary);
  line-height: 1;
}

.legend-description {
  font-size: 0.65rem;
  color: var(--an-text-muted);
  line-height: 1.1;
}

.legend-footer {
  border-top: 1px solid #dee2e6;
  padding-top: 8px;
  margin-top: 8px;
}

.color-guide {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.color-indicator {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  display: inline-block;
}

.positive-color {
  background: #28a745;
}

.negative-color {
  background: #dc3545;
}

.color-text {
  font-size: 0.7rem;
  color: var(--an-text-muted);
  font-weight: 500;
}

.total-indicator {
  font-size: 0.7rem;
  color: var(--an-text-muted);
  font-weight: 600;
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid #dee2e6;
}

/* Responsive adjustments for legend */
@media (max-width: 900px) {
  .legend-items {
    flex-direction: column;
    gap: 8px;
  }

  .legend-item-modern {
    justify-content: flex-start;
    gap: 12px;
  }

  .color-guide {
    justify-content: flex-start;
    gap: 6px;
  }

  .total-indicator {
    margin-left: 0;
    padding-left: 0;
    border-left: none;
    margin-top: 4px;
  }
}

/* Background colors for owned vs unowned sections */
.owned-sectors-bg {
  background: linear-gradient(135deg, #f0f8f5 0%, #e8f5f0 100%);
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #d4edda;
}

.unowned-sectors-bg {
  background: linear-gradient(135deg, #f8f4f0 0%, #f5f0e8 100%);
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #f5c6cb;
}

.owned-chart-bg {
  background: linear-gradient(135deg, #f0f8f5 0%, #e8f5f0 100%);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #d4edda;
}

.unowned-chart-bg {
  background: linear-gradient(135deg, #f8f4f0 0%, #f5f0e8 100%);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #f5c6cb;
}

.waterfall-split-better {
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 24px;
  box-sizing: border-box;
}
.split-titles {
  display: flex;
  justify-content: space-between;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0px;
  gap: 20px;
}
.split-title-left {
  min-width: 180px;
  text-align: center;
  transition: width 0.3s ease;
}
.split-title-right {
  min-width: 180px;
  text-align: center;
  transition: width 0.3s ease;
}
.split-grids {
  display: flex;
  gap: 20px;
  margin-bottom: 8px;
  width: 100%;
}
.sector-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
  align-items: start;
  width: 100%;
  min-width: 0;
  transition: flex 0.3s ease;
}
/* Single column layout for sparse data (3 or fewer items) */
.sector-grid.sparse-grid {
  grid-template-columns: 1fr;
  max-width: 400px;
  margin: 0 auto;
}
@media (max-width: 900px) {
  .sector-grid {
    grid-template-columns: 1fr;
  }
  .sector-grid.sparse-grid {
    max-width: 100%;
  }
}
.sector-grid-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  width: 100%;
  min-width: 0;
  word-break: break-word;
  flex-wrap: wrap;
  box-sizing: border-box;
  background: none;
  border: none;
  border-radius: 0;
  margin-bottom: 0;
  padding: 0;
}
.sector-grid-item > div:first-child {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
}
.sector-name {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 0.85rem;
  min-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  white-space: normal;
  max-width: 220px;
}
.sector-value {
  color: #222;
  min-width: 60px;
  font-size: 0.85rem;
}
.badge.concentration-badge {
  margin-left: 0;
  margin-right: 0;
  font-size: 0.68rem;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 700;
  background: #ff6b6b;
  color: #fff;
  writing-mode: initial;
  letter-spacing: normal;
}
.sector-stocks-row {
  font-size: 0.72rem;
  color: #888;
  margin-top: 2px;
  white-space: normal;
  word-break: break-word;
}
.split-charts-better {
  display: flex;
  flex-direction: row;
  gap: 20px;
  width: 100%;
}
.split-panel-better.left-panel-better,
.split-panel-better.right-panel-better {
  flex: 1 1 0;
  min-width: 0;
  transition: flex 0.3s ease;
}
@media (max-width: 900px) {
  .split-charts-better {
    flex-direction: row;
  }
  .split-panel-better.left-panel-better, .split-panel-better.right-panel-better {
    max-width: 50%;
  }

  /* Maintain background styling on mobile */
  .owned-sectors-bg, .unowned-sectors-bg {
    padding: 8px;
    margin-bottom: 8px;
  }

  .owned-chart-bg, .unowned-chart-bg {
    padding: 12px;
  }
}

@media (max-width: 768px) {
  .split-charts-better {
    flex-direction: column;
    gap: 16px;
  }

  .split-panel-better.left-panel-better,
  .split-panel-better.right-panel-better {
    max-width: 100%;
    flex: 1 1 100% !important;
  }

  .split-grids {
    flex-direction: column;
    gap: 12px;
  }

  .owned-sectors-bg, .unowned-sectors-bg {
    width: 100%;
    flex: 1 1 100% !important;
  }

  .split-title-left, .split-title-right {
    width: 100% !important;
  }
}

/* Add dot and legend styles */
.dot.concentration-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff6b6b;
  margin-left: 4px;
  margin-right: 2px;
}
.sector-legend-inline {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: #888;
  margin-top: 2px;
  margin-bottom: 2px;
}
.legend-label {
  font-size: 0.78rem;
  color: #888;
}
.sector-legend-inline-restyled {
  display: flex;
  align-items: center;
  gap: 2.4px;
  font-size: 0.72rem;
  color: #888;
  font-style: italic;
  font-weight: 400;
  margin: 0;
}
.legend-label-restyled {
  font-size: 0.72rem;
  color: #888;
  font-style: italic;
  font-weight: 400;
}
.right-align-grid {
  justify-items: end;
}
.left-grid .sector-grid-item > div:first-child,
.right-grid .sector-grid-item > div:first-child {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Attribution Explainer Accordion */
.attribution-explainer {
  margin-top: 40px;
  margin-bottom: 20px;
}

.explainer-accordion {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #dee2e6;
  border-radius: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.3s ease;
}

.explainer-accordion[open] {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.explainer-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  cursor: pointer;
  list-style: none;
  user-select: none;
  transition: background-color 0.2s ease;
}

.explainer-summary::-webkit-details-marker {
  display: none;
}

.explainer-summary:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.explainer-heading {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--an-text-primary);
  letter-spacing: -0.01em;
}

.explainer-icon {
  font-size: 0.75rem;
  color: var(--an-text-muted);
  transition: transform 0.3s ease;
}

.explainer-accordion[open] .explainer-icon {
  transform: rotate(180deg);
}

.explainer-content {
  padding: 0 24px 24px 24px;
  border-top: 1px solid #e9ecef;
  margin-top: 0;
}

.explainer-block {
  margin-top: 20px;
}

.explainer-block:first-child {
  margin-top: 16px;
}

.explainer-block-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 6px 0;
}

.explainer-block-text {
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--an-text-primary);
  margin: 0;
}

.explainer-block-italic {
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--an-text-muted);
  margin: 6px 0 0 0;
  font-style: italic;
}

.explainer-footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  font-size: 0.8rem;
  color: var(--an-text-muted);
  line-height: 1.5;
  font-style: italic;
}

/* Responsive adjustments for explainer */
@media (max-width: 768px) {
  .attribution-explainer {
    margin-top: 30px;
  }

  .explainer-summary {
    padding: 14px 16px;
  }

  .explainer-heading {
    font-size: 0.95rem;
  }

  .explainer-content {
    padding: 0 16px 16px 16px;
  }

  .explainer-block-title {
    font-size: 0.9rem;
  }

  .explainer-block-text,
  .explainer-block-italic {
    font-size: 0.825rem;
  }

  .explainer-footer {
    font-size: 0.75rem;
  }
}
</style>
